import type { ComponentChildren } from 'preact';
import { ITEMS, RARITY_COLOR } from '../../game/items';
import { genericIcon } from '../../ui/icons';
import type { Glyph } from './data';
import './widgets.css';

/** Small presentational helpers shared by the Haven, Bonds and life-skill UIs. */

const hex = (n: number) => `#${n.toString(16).padStart(6, '0')}`;
export const cssHue = hex;

export function itemIconUrl(id: string): string {
  const def = ITEMS[id];
  if (!def) return genericIcon(`item:${id}`, 0x777777, 'orb');
  return genericIcon(`item:${id}`, def.icon.hue, def.icon.glyph as Glyph);
}

export function itemName(id: string): string {
  return ITEMS[id]?.name ?? id;
}

export function itemColor(id: string): string {
  const def = ITEMS[id];
  return def ? RARITY_COLOR[def.rarity] : '#fff';
}

/** Rarity-framed square item icon with an optional count badge. */
export function ItemIcon({ id, count, size = 38, dim = false, title }: { id: string; count?: number | string; size?: number; dim?: boolean; title?: string }) {
  const def = ITEMS[id];
  return (
    <span
      class={`fx-icon rbg-${def?.rarity ?? 'normal'}${dim ? ' dim' : ''}`}
      style={{ width: size, height: size }}
      title={title ?? (def ? `${def.name}${def.description ? ` — ${def.description}` : ''}` : id)}
    >
      <img src={itemIconUrl(id)} alt="" draggable={false} />
      {count !== undefined && <span class="fx-n">{count}</span>}
    </span>
  );
}

/** Icon + rarity-coloured name + count, for reward lists. */
export function ItemLine({ id, count, have }: { id: string; count: number; have?: number }) {
  const short = have !== undefined && have < count;
  return (
    <div class="fx-line">
      <ItemIcon id={id} size={26} />
      <span class="fx-line-name" style={{ color: itemColor(id) }}>
        {itemName(id)}
      </span>
      <span class={`fx-line-n${short ? ' short' : have !== undefined ? ' ok' : ''}`}>{have !== undefined ? `${Math.min(have, 9999)}/${count}` : `×${count}`}</span>
    </div>
  );
}

/** Thin horizontal progress bar (0..1). */
export function Bar({ value, tone = 'gold', label }: { value: number; tone?: 'gold' | 'blue' | 'green' | 'pink' | 'teal'; label?: ComponentChildren }) {
  const v = Math.max(0, Math.min(1, value));
  return (
    <div class={`fx-bar fx-bar--${tone}`}>
      <i style={{ width: `${(v * 100).toFixed(2)}%` }} />
      {label !== undefined && <span>{label}</span>}
    </div>
  );
}

export function Silver({ n }: { n: number }) {
  return (
    <span class="fx-coin">
      <i class="coin silver" />
      {n.toLocaleString('en-US')}
    </span>
  );
}

export function Gold({ n }: { n: number }) {
  return (
    <span class="fx-coin">
      <i class="coin gold" />
      {n.toLocaleString('en-US')}
    </span>
  );
}

export function Keycap({ k }: { k: string }) {
  return <span class="keycap">{k}</span>;
}
